import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { LanguageService } from '../language/language.service';
import { SalesOrg } from '../model/sales-org';

@Injectable({
  providedIn: 'root'
})
export class SalesOrgService {
  private http = inject(HttpClient);
  private languageService = inject(LanguageService);


  private serviceUrl: string = environment.salesOrgUrl;
  private httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  public salesOrgs: SalesOrg[];
  public salesOrgBySourceMap = new Map<string, SalesOrg[]>();
  public distinctSources: Set<string>;
  public distinctSalesOrgs: SalesOrg[];
  public distinctSalesOrgsSignal = signal<SalesOrg[]>([]);
  public salesOrgsLoaded = signal<boolean>(false);
  public defaultSalesOrg: string = null;
  public defaultSalesOrgUpdated = new Subject<string>();


  constructor() {
    this.loadSalesOrgs();
  }

  public loadSalesOrgs(): void {
    this.salesOrgsLoaded.set(false);
    this.http.get<SalesOrg[]>(this.serviceUrl + '/'+this.languageService.getCurrentIsoCode()).subscribe({
      next: data => {
        this.salesOrgs = data.map(item => Object.assign(new SalesOrg(), item));
        this.distinctSources = new Set(this.salesOrgs.map(salesOrg => salesOrg.source));
        this.distinctSalesOrgs = this.getDistinctSalesOrgs();
        this.distinctSalesOrgsSignal.set(this.distinctSalesOrgs);

        this.salesOrgBySourceMap.clear();
        this.distinctSources.forEach(source => {
          const salesOrgsForSource = this.salesOrgs.filter(salesOrg => salesOrg.source === source);
          this.salesOrgBySourceMap.set(source, salesOrgsForSource);
        });
        this.salesOrgsLoaded.set(true);
      },
      error: () => {
        // Keep UI stable even when loading fails.
        this.salesOrgs = [];
        this.distinctSources = new Set();
        this.distinctSalesOrgs = [];
        this.distinctSalesOrgsSignal.set([]);
        this.salesOrgBySourceMap.clear();
        this.salesOrgsLoaded.set(true);
      }
    });
  }

  public getDefaultSalesOrg(): string {
    return this.defaultSalesOrg;
  }

  updateDefaultSalesOrg() {
    this.defaultSalesOrgUpdated.next(this.getDefaultSalesOrg());
    this.insertUrlParam('defaultSalesOrg', this.getDefaultSalesOrg());
  }

  public setDefaultSalesOrg(salesOrg: string): void {
    this.defaultSalesOrg = salesOrg;
    this.updateDefaultSalesOrg();
  }

  insertUrlParam(key, value) {
    if (history.pushState) {
      const searchParams = new URLSearchParams(window.location.hash.replace('#/', ''));
      searchParams.set(key, value);
      const newUrl = window.location.protocol + '//' + window.location.host + window.location.pathname + '#/?' + searchParams.toString();
      window.history.pushState({path: newUrl}, '', newUrl);
    }
  }

  public isValidCombination(salesOrg: string, source: string): boolean {
    const tmpSource = source === null ? '' : source;
    return this.salesOrgs.some(s => s.id === salesOrg && s.source === tmpSource);
  }

  public getSalesOrgsBySource(source: string): SalesOrg[] {
    const tmpSource = source === null ? '' : source;
    return this.salesOrgBySourceMap.get(tmpSource);
  }

  public getDistinctSalesOrgs(): SalesOrg[] {
    // return array of sales orgs with distinct id
    return this.salesOrgs.filter((salesOrg, index, self) =>
      index === self.findIndex((t) => (
        t.id === salesOrg.id
      ))
    );
  }
}
